import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { CellAction } from './peopleGrid';
import { crewLink } from './staffing';

/** Owner 2026-10-27 12:37: a tiny choice under one item (the places of „Obsadź” — „Wesele A – Technik”). */
export type MenuChoices = { after: CellAction['id']; label: string; items: Array<{ key: string; label: string }>; onPick: (key: string) => void };

/**
 * Owner 2026-10-26 07:49: the one menu of a person's cell in „Dostępność → Ludzie” — „Michał · 31.10 · Wesele A” and
 * the actions that apply (see `cellActions`). Placed by the clicked cell; Esc or a click outside closes it. `note` — a
 * muted line at the top (owner 2026-10-27 23:31: a past day says why it cannot be staffed or asked about).
 */
export function CellMenu({ anchor, head, note, event, actions, busy, choices, onPick, onClose }: { anchor: HTMLElement; head: string; note?: string; event?: { eventId?: string; eventName: string }; actions: CellAction[]; busy: boolean; choices?: MenuChoices; onPick: (a: CellAction) => void; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  useLayoutEffect(() => {
    const r = anchor.getBoundingClientRect();
    const width = ref.current?.offsetWidth ?? 220;
    const height = ref.current?.offsetHeight ?? 240;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    const below = r.bottom + 4;
    setPos({ top: below + height > window.innerHeight - 8 ? Math.max(8, r.top - height - 4) : below, left });
  }, [anchor]);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
      anchor.focus();
    };
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node) && !anchor.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onDown);
    };
  }, [anchor, onClose]);
  const move = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = [...(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const at = items.indexOf(document.activeElement as HTMLElement);
    items[(at + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length]?.focus();
  };
  return (
    <div ref={ref} className="pg-menu" role="menu" aria-label={head} style={{ top: pos.top, left: pos.left }} onKeyDown={move}>
      <div className="pg-menu-head">
        {head}
        {event && (
          <>
            {' · '}
            {event.eventId ? <Link to={crewLink(event.eventId)}>{event.eventName}</Link> : event.eventName}
          </>
        )}
      </div>
      {note && <p className="pg-menu-note">{note}</p>}
      {actions.map((a) => (
        <Fragment key={`${a.id}:${a.assignmentId ?? ''}`}>
          <button type="button" role="menuitem" disabled={busy && a.id !== 'history'} onClick={() => onPick(a)}>
            {a.label}
          </button>
          {choices && choices.after === a.id && (
            <div className="pg-menu-choices" role="group" aria-label={choices.label}>
              {choices.items.map((c) => (
                <button key={c.key} type="button" role="menuitem" onClick={() => choices.onPick(c.key)}>
                  {c.label}
                </button>
              ))}
            </div>
          )}
        </Fragment>
      ))}
    </div>
  );
}
